import { VENUES, type GameId } from '@tira/protocol';
import type { JSX } from 'react';
import { useLang, useT } from '../i18n/index.js';
import { socket } from '../net/socket.js';
import { useApp } from '../state/store.js';
import { DamaBoard } from './games/DamaBoard.js';
import { GeraBoard } from './games/GeraBoard.js';
import { MahjongBoard } from './games/MahjongBoard.js';
import { PusoyBoard } from './games/PusoyBoard.js';
import { PusoyDosBoard } from './games/PusoyDosBoard.js';
import { TongitsBoard } from './games/TongitsBoard.js';
import { useSeatWait } from './lib/waiting.js';
import { GameRoom } from './parts/GameRoom.js';

/**
 * The in-match route. It knows two things: which board belongs to this venue,
 * and what to show before one has anything to draw.
 *
 * Everything else — the station, the camera, the seats, the pot, table talk,
 * the action rail — belongs to GameRoom, which every board mounts itself. That
 * is why a board can be read top to bottom without a shell in the way, and why
 * the 2.5D and top cameras are one implementation rather than six.
 */
export function Table({ tableId }: { tableId: string }): JSX.Element {
  const table = useApp((s) => (s.table?.tableId === tableId ? s.table : null));
  const match = useApp((s) => s.match);
  const t = useT();
  const wait = useSeatWait(table, useLang());

  const game = table?.game ?? match?.game ?? null;
  const board = match ? boardFor(game) : null;
  if (board) return board;

  const { filled, empty } = wait;
  const seatCount = table?.seats.length ?? 0;

  /*
   * Sitting at a table that is not filling. Public tables get a bot backfill
   * from matchmaking, but a Kubo gets none at all and a table somebody left
   * gets none either — so this was a room you could only leave. The way out is
   * to fill the empty chairs yourself.
   */
  const canFill = table !== null && match === null && empty > 0;

  return (
    <GameRoom
      felt={
        <div className="room__wait">
          {match ? (
            <>
              <b>
                {t('room.wait.nogame', {
                  venue: game ? t(`venue.${game}`) : t('table.thisgame'),
                })}
              </b>
              <span>{t('room.wait.nogame.sub')}</span>
            </>
          ) : (
            <>
              <b>{t('room.wait.title')}</b>
              <span>
                {table
                  ? `${t('room.wait.seated', { filled, total: seatCount })} ${wait.line}`
                  : wait.line}
              </span>
            </>
          )}
        </div>
      }
      actions={
        canFill ? (
          <button
            className="room-act"
            data-tone="primary"
            type="button"
            onClick={() => socket.send({ t: 'table.bots', tableId })}
          >
            {t('room.fill', { n: empty })}
          </button>
        ) : null
      }
    />
  );
}

/**
 * Game delegation. One board per venue, and null for a venue whose engine has
 * not shipped — the route shows its "malapit na" state rather than mounting a
 * board that would read a view nobody sends.
 *
 * This is the only thing the route knows about any game. Everything past this
 * line — the view, the legal actions, the wire — belongs to the board.
 */
function boardFor(game: GameId | null): JSX.Element | null {
  switch (game) {
    case 'dama':
      return <DamaBoard />;
    case 'tongits':
      return <TongitsBoard />;
    case 'pusoydos':
      return <PusoyDosBoard />;
    case 'mahjong':
      return <MahjongBoard />;
    case 'pusoy':
      return <PusoyBoard />;
    case 'gera':
      return <GeraBoard />;
    default:
      return null;
  }
}
